import { useState } from 'react'
import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'
import { useTaskPolling } from '@/hooks/useTaskPolling'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type PromotionListOutput = OutputRouter['promotions']['list']
export type PromotionItem = PromotionListOutput['items'][number]
export type PromotionDetails = OutputRouter['promotions']['getById']
export type PromotionStoreListOutput = OutputRouter['promotions']['listForStore']
export type PromotionStoreItem = PromotionStoreListOutput['promotions'][number]
export type SyncPromotionsOutput = OutputRouter['promotions']['syncFromStorefront']
export type CreatePromotionOutput = OutputRouter['promotions']['create']
export type UpdatePromotionOutput = OutputRouter['promotions']['update']
export type DeletePromotionsOutput = OutputRouter['promotions']['delete']

// Input types
export type ListPromotionsInput = InputRouter['promotions']['list']
export type GetPromotionInput = InputRouter['promotions']['getById']
export type ListStorePromotionsInput = InputRouter['promotions']['listForStore']
export type SyncPromotionsInput = InputRouter['promotions']['syncFromStorefront']
export type CreatePromotionInput = InputRouter['promotions']['create']
export type UpdatePromotionInput = InputRouter['promotions']['update']
export type DeletePromotionsInput = InputRouter['promotions']['delete']

// Query hooks
export const usePromotionsList = (params: ListPromotionsInput) => {
    return trpc.promotions.list.useQuery(params, {
        enabled: !!params.storeId,
    })
}

export const usePromotion = (id: string, storeId: string, enabled = true) => {
    return trpc.promotions.getById.useQuery(
        { id, storeId },
        {
            enabled: enabled && !!id && !!storeId,
            // Inherit the global retry policy (no retry on 401/403): a transient
            // session-refresh 401 is now recovered by the tRPC client's refresh-and-
            // retry link, so retrying here with the same stale token only hammered
            // the server. See createTRPCClient in ./trpc.ts.
            staleTime: 30000, // Consider data fresh for 30 seconds
        },
    )
}

export const useStorePromotions = (params: ListStorePromotionsInput, enabled = true) => {
    return trpc.promotions.listForStore.useQuery(params, {
        enabled: enabled && !!params.storeId,
    })
}

// Mutation hooks
export const useSyncPromotionsFromStorefront = () => {
    const { toast } = useToast()

    return trpc.promotions.syncFromStorefront.useMutation({
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to sync promotions',
                variant: 'destructive',
            })
        },
    })
}

/**
 * Promotion sync with background-task polling.
 *
 * The backend may run the sync inline (small stores) or off-load it to Inngest
 * and return a `taskId` (large stores). This hook abstracts both:
 *  - inline path → immediate success toast + cache invalidation
 *  - background path → "started" toast, then poll until `completed`/`failed`
 *    via `useTaskPolling` and toast/invalidate at the real terminal state.
 *
 * `isLoading` covers both the mutation request and the polling window so the
 * caller can keep the button disabled until the sync is actually done.
 */
export const usePromotionSyncWithPolling = (onSyncComplete?: () => void) => {
    const { toast } = useToast()
    const utils = trpc.useUtils()
    const syncMutation = useSyncPromotionsFromStorefront()
    const [currentTaskId, setCurrentTaskId] = useState<string | null>(null)

    const invalidatePromotions = () => {
        utils.promotions.list.invalidate()
        utils.promotions.listForStore.invalidate()
    }

    const { isPolling } = useTaskPolling({
        taskId: currentTaskId,
        onCompleted: (output) => {
            const total = output?.stats?.totalCount ?? 0
            const skippedCount = output?.stats?.skippedCount ?? 0
            const skippedTypes = (output?.stats?.skippedTypes as string[] | undefined) ?? []
            const skippedSuffix =
                skippedCount > 0
                    ? ` Skipped ${skippedCount} unsupported (${skippedTypes.join(', ') || 'unknown types'}).`
                    : ''
            toast({
                title: 'Success',
                description: `Promotion sync completed! ${total} promotion${total === 1 ? '' : 's'} synced.${skippedSuffix}`,
            })
            invalidatePromotions()
            setCurrentTaskId(null)
            onSyncComplete?.()
        },
        onFailed: (error) => {
            toast({
                title: 'Error',
                description: `Promotion sync failed: ${error}`,
                variant: 'destructive',
            })
            setCurrentTaskId(null)
        },
    })

    const handleSync = (input: SyncPromotionsInput) => {
        if (syncMutation.isPending || isPolling) return

        syncMutation.mutate(input, {
            onSuccess: (data) => {
                if (data.taskId) {
                    toast({
                        title: 'Sync started',
                        description: data.message || 'Promotion sync started in background',
                    })
                    setCurrentTaskId(data.taskId)
                    return
                }

                toast({
                    title: 'Success',
                    description: data.message || 'Promotions synced successfully',
                })
                invalidatePromotions()
                onSyncComplete?.()
            },
        })
    }

    return {
        handleSync,
        isLoading: syncMutation.isPending || isPolling,
    }
}

export const useBulkCreatePromotions = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.promotions.bulkCreate.useMutation({
        onSuccess: (data) => {
            utils.promotions.list.invalidate()
            utils.promotions.listForStore.invalidate()
            toast({
                title: 'Success',
                description: `${data.length} promotion(s) created successfully`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to create promotions',
                variant: 'destructive',
            })
        },
    })
}

export const useCreatePromotion = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.promotions.create.useMutation({
        onSuccess: () => {
            utils.promotions.list.invalidate()
            utils.promotions.listForStore.invalidate()
            toast({
                title: 'Success',
                description: 'Promotion created successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to create promotion',
                variant: 'destructive',
            })
        },
    })
}

export const useUpdatePromotion = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.promotions.update.useMutation({
        onSuccess: () => {
            utils.promotions.list.invalidate()
            utils.promotions.getById.invalidate()
            utils.promotions.listForStore.invalidate()
            toast({
                title: 'Success',
                description: 'Promotion updated successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to update promotion',
                variant: 'destructive',
            })
        },
    })
}

export const useDeletePromotions = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.promotions.delete.useMutation({
        onSuccess: () => {
            utils.promotions.list.invalidate()
            utils.promotions.listForStore.invalidate()
            toast({
                title: 'Success',
                description: 'Promotion(s) deleted successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to delete promotion(s)',
                variant: 'destructive',
            })
        },
    })
}

